import React, { useState, useEffect, useCallback } from 'react';
import { MapPin, Search, Navigation, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

// Format address from Nominatim result
const formatAddress = (result) => {
  const addr = result.address || {};
  const parts = [];
  
  // Road/Street
  if (addr.road) parts.push(addr.road);
  
  // Suburb/Neighbourhood/Locality
  const locality = addr.suburb || addr.neighbourhood || addr.locality || addr.hamlet;
  if (locality) parts.push(locality);
  
  // City/Town/Village
  const city = addr.city || addr.town || addr.village || addr.county;
  if (city && city !== locality) parts.push(city);
  
  // State
  if (addr.state) parts.push(addr.state);
  
  // Pincode
  if (addr.postcode) parts.push(addr.postcode);
  
  return parts.length > 0 ? parts.join(', ') : result.display_name.split(',').slice(0, 4).join(', ');
};

const LocationSelector = ({ isOpen, onClose, onSelectLocation }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [geoLoading, setGeoLoading] = useState(false);

  // Debounced search using Nominatim - Maharashtra only
  useEffect(() => {
    if (!searchQuery || searchQuery.length < 3) {
      setSearchResults([]);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setLoading(true);
      try {
        // Append Maharashtra to query for better results
        const queryWithState = `${searchQuery}, Maharashtra`;
        const response = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(queryWithState)}&addressdetails=1&limit=8&countrycodes=in`,
          {
            headers: {
              'Accept': 'application/json',
              'User-Agent': 'FoodMatrix/1.0'
            }
          }
        );
        const data = await response.json();
        
        // Filter to keep only Maharashtra results
        const maharashtraResults = data.filter(result => {
          const state = result.address?.state || '';
          const displayName = result.display_name || '';
          return state.toLowerCase().includes('maharashtra') || 
                 displayName.toLowerCase().includes('maharashtra');
        });
        
        setSearchResults(maharashtraResults.slice(0, 8));
      } catch (error) {
        console.error('Search error:', error);
        toast.error('Failed to search location');
      } finally {
        setLoading(false);
      }
    }, 400);

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const handleSelectResult = (result) => {
    const addr = result.address || {};
    const formattedLabel = formatAddress(result);
    
    const locationData = {
      lat: parseFloat(result.lat),
      lng: parseFloat(result.lon),
      label: formattedLabel,
      pincode: addr.postcode || null
    };
    
    localStorage.setItem('userLocationLat', locationData.lat.toString());
    localStorage.setItem('userLocationLng', locationData.lng.toString());
    localStorage.setItem('userLocationLabel', locationData.label);
    if (locationData.pincode) {
      localStorage.setItem('userLocationPincode', locationData.pincode);
    }
    
    onSelectLocation(locationData);
    onClose();
    toast.success('Location updated');
  };

  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation not supported by your browser');
      return;
    }

    setGeoLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        
        // Reverse geocode to get address
        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1`,
            {
              headers: {
                'Accept': 'application/json',
                'User-Agent': 'FoodMatrix/1.0'
              }
            }
          );
          const data = await response.json();
          
          const locationData = {
            lat: latitude,
            lng: longitude,
            label: data.display_name ? data.display_name.split(',').slice(0, 3).join(', ') : 'Current Location'
          };
          
          localStorage.setItem('userLocationLat', locationData.lat.toString());
          localStorage.setItem('userLocationLng', locationData.lng.toString());
          localStorage.setItem('userLocationLabel', locationData.label);
          
          onSelectLocation(locationData);
          onClose();
          toast.success('Using current location');
        } catch (error) {
          console.error('Reverse geocode error:', error);
          // Still use coordinates even if reverse geocode fails
          const locationData = {
            lat: latitude,
            lng: longitude,
            label: 'Current Location'
          };
          
          localStorage.setItem('userLocationLat', locationData.lat.toString());
          localStorage.setItem('userLocationLng', locationData.lng.toString());
          localStorage.setItem('userLocationLabel', locationData.label);
          
          onSelectLocation(locationData);
          onClose();
          toast.success('Using current location');
        } finally {
          setGeoLoading(false);
        }
      },
      (error) => {
        setGeoLoading(false);
        switch (error.code) {
          case error.PERMISSION_DENIED:
            toast.error('Location permission denied');
            break;
          case error.POSITION_UNAVAILABLE:
            toast.error('Location unavailable');
            break;
          case error.TIMEOUT:
            toast.error('Location request timed out');
            break;
          default:
            toast.error('Failed to get location');
        }
      },
      { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
    );
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div 
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 max-h-[80vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="bg-gradient-to-r from-green-600 to-emerald-600 text-white p-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <MapPin className="w-5 h-5" />
            <h2 className="font-bold text-lg">Select Delivery Location</h2>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-white/20 rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Current Location Button */}
          <Button
            onClick={handleUseCurrentLocation}
            disabled={geoLoading}
            className="w-full bg-green-50 hover:bg-green-100 text-green-700 border border-green-200 py-3 rounded-xl flex items-center justify-center gap-2"
            data-testid="use-current-location-btn"
          >
            <Navigation className={`w-5 h-5 ${geoLoading ? 'animate-pulse' : ''}`} />
            {geoLoading ? 'Getting location...' : 'Use Current Location'}
          </Button>

          <div className="flex items-center gap-2 text-gray-400 text-sm">
            <div className="flex-1 h-px bg-gray-200" />
            <span>or search</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              type="text"
              placeholder="Search area, city, or pincode in Maharashtra..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 py-3 rounded-xl border-2 border-gray-200 focus:border-green-400"
              data-testid="location-search-input"
            />
          </div>

          {/* Results */}
          <div className="max-h-60 overflow-y-auto space-y-2">
            {loading && (
              <div className="text-center py-4 text-gray-500">
                <div className="w-6 h-6 border-2 border-green-500 border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                Searching in Maharashtra...
              </div>
            )}

            {!loading && searchResults.length > 0 && searchResults.map((result, index) => {
              const addr = result.address || {};
              const locality = addr.road || addr.suburb || addr.neighbourhood || addr.locality || '';
              const area = addr.city || addr.town || addr.village || addr.county || '';
              const pincode = addr.postcode || '';
              
              return (
                <button
                  key={index}
                  onClick={() => handleSelectResult(result)}
                  className="w-full text-left p-3 rounded-xl hover:bg-green-50 border border-gray-100 transition-colors"
                  data-testid={`location-result-${index}`}
                >
                  <div className="flex items-start gap-3">
                    <MapPin className="w-5 h-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-gray-800 text-sm">
                        {locality || area || result.display_name.split(',')[0]}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {[area, 'Maharashtra', pincode].filter(Boolean).join(', ')}
                      </p>
                    </div>
                  </div>
                </button>
              );
            })}

            {!loading && searchQuery.length >= 3 && searchResults.length === 0 && (
              <div className="text-center py-4 text-gray-500">
                No results found in Maharashtra
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LocationSelector;
